import { useMemo, useState } from 'react';
import { useDataStore } from '../stores/dataStore';
import type { FinancialRecord } from '../types';

interface OverviewProps {
  chain: string;
}

export default function Overview({ chain }: OverviewProps) {
  const { nodes, edges, companies, rankings, financials, companyIndex } = useDataStore();
  const [showAShareOnly, setShowAShareOnly] = useState(false);

  const filteredRankings = useMemo(() => chain === 'all'
    ? rankings
    : rankings.filter(r => {
        const company = companyIndex.get(r.company_id);
        return company?.chains?.includes(chain);
      }), [rankings, companyIndex, chain]);

  const investableRankings = useMemo(() => {
    let result = filteredRankings.filter(r => {
      const company = companyIndex.get(r.company_id);
      return company?.coverage_role === 'core_investable';
    });
    if (showAShareOnly) {
      result = result.filter(r => {
        const company = companyIndex.get(r.company_id);
        return company?.exchange === 'A股';
      });
    }
    return result;
  }, [filteredRankings, companyIndex, showAShareOnly]);

  const highBottleneckNodes = nodes.filter(n => n.bottleneck_level === 'high');
  const latestFinancials = financials.reduce((acc, f) => {
    if (!acc[f.company_id]) acc[f.company_id] = f;
    return acc;
  }, {} as Record<string, FinancialRecord>);

  return (
    <div style={{ padding: '24px', overflow: 'auto', height: '100%', background: '#f8fafc', color: '#1f2937' }}>
      {/* 统计卡片 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
        {[
          { label: '公司总数', value: companies.length, sub: `${companies.filter(c => c.coverage_role === 'core_investable').length}家可投资` },
          { label: '产业链节点', value: nodes.length, sub: `${highBottleneckNodes.length}个高瓶颈` },
          { label: '链路关系', value: edges.length, sub: '条上下游关系' },
          { label: '覆盖期间', value: 'FY2022-2026', sub: '5年财务数据' },
        ].map(card => (
          <div key={card.label} style={{
            padding: '16px', borderRadius: '8px', border: '1px solid #e5e7eb',
            background: 'white',
          }}>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>{card.label}</div>
            <div style={{ fontSize: '24px', fontWeight: 700, marginTop: '4px' }}>{card.value}</div>
            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{card.sub}</div>
          </div>
        ))}
      </div>

      {/* 高瓶颈节点 */}
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ fontSize: '14px', margin: '0 0 12px' }}>关键瓶颈节点</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {highBottleneckNodes.map(n => (
            <span key={n.id} style={{
              padding: '4px 12px', borderRadius: '12px', fontSize: '12px',
              background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca',
            }}>
              {n.name}
            </span>
          ))}
        </div>
      </div>

      {/* Top公司排名 */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <h3 style={{ fontSize: '14px', margin: 0 }}>
            投资研究优先级 Top 20 {chain !== 'all' ? `(${chain === 'nvidia' ? '英伟达链' : chain === 'google' ? '谷歌链' : '华为链'})` : ''}
          </h3>
          <button onClick={() => setShowAShareOnly(!showAShareOnly)} style={{
            padding: '4px 12px', borderRadius: '4px', fontSize: '12px',
            border: showAShareOnly ? '2px solid #8b5cf6' : '1px solid #d1d5db',
            background: showAShareOnly ? '#f5f3ff' : 'white', cursor: 'pointer',
          }}>
            {showAShareOnly ? '仅A股' : '全部市场'}
          </button>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ textAlign: 'left', padding: '8px' }}>排名</th>
              <th style={{ textAlign: 'left', padding: '8px' }}>公司</th>
              <th style={{ textAlign: 'left', padding: '8px' }}>代码</th>
              <th style={{ textAlign: 'left', padding: '8px' }}>市场</th>
              <th style={{ textAlign: 'right', padding: '8px' }}>IPS</th>
              <th style={{ textAlign: 'right', padding: '8px' }}>收入</th>
              <th style={{ textAlign: 'right', padding: '8px' }}>毛利率</th>
            </tr>
          </thead>
          <tbody>
            {investableRankings.slice(0, 20).map((r, i) => {
              const company = companies.find(c => c.company_id === r.company_id);
              const fin = latestFinancials[r.company_id];
              return (
                <tr key={r.company_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '6px 8px', fontWeight: 600 }}>{i + 1}</td>
                  <td style={{ padding: '6px 8px' }}>{company?.company || r.company_id}</td>
                  <td style={{ padding: '6px 8px', color: '#6b7280' }}>{company?.ticker || ''}</td>
                  <td style={{ padding: '6px 8px', color: '#6b7280' }}>{company?.exchange || ''}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>{r.ips_score.toFixed(1)}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: '#6b7280' }}>
                    {fin?.revenue_latest?.toFixed(1) || '-'}
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: '#6b7280' }}>
                    {fin?.gross_margin?.toFixed(1) || '-'}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
